// Web push on this device (#3756, ADR 0041 decision 5; founder pick C1).
// The browser's own permission prompt is spent ONLY after the student said
// "Yes, tell me" in our sheet: a "Not now" here never burns the one-time
// browser prompt. The server stores the subscription per account and device
// and deletes it on opt-out; this file never logs an endpoint or its keys.
import { getPushConfig, pushSubscribe, pushUnsubscribe } from './api';

export const isIOS = () => {
    try {
        return /iPhone|iPad|iPod/i.test(navigator.userAgent || '')
            || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    } catch { return false; }
};
export const isStandalone = () => {
    try {
        return window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;
    } catch { return false; }
};

/** 'supported' | 'needs_install' (iPhone Safari, not on the Home Screen) | 'unsupported'. */
export function pushSupport() {
    try {
        if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return isIOS() ? 'needs_install' : 'unsupported';
        if (isIOS() && !isStandalone()) return 'needs_install';
        if (!('PushManager' in window) || !('Notification' in window)) return isIOS() ? 'needs_install' : 'unsupported';
        return 'supported';
    } catch { return 'unsupported'; }
}

export const permission = () => {
    try { return window.Notification ? window.Notification.permission : 'default'; } catch { return 'default'; }
};

/** A short, human device name for the Settings row. */
export function deviceLabel() {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    if (/iPhone/i.test(ua)) return 'iPhone';
    if (/iPad/i.test(ua)) return 'iPad';
    if (/Android/i.test(ua)) return 'Android phone';
    if (/Macintosh/i.test(ua)) return 'Mac';
    if (/Windows/i.test(ua)) return 'Windows computer';
    if (/CrOS/i.test(ua)) return 'Chromebook';
    return 'this device';
}

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function registration() {
    // main.jsx registers /sw.js in production; register here too so a
    // development build (and the first ask) never waits on a missing worker.
    const existing = await navigator.serviceWorker.getRegistration('/');
    return existing || navigator.serviceWorker.register('/sw.js');
}

/** This device's subscription, or null. */
export async function currentSubscription() {
    try {
        if (pushSupport() !== 'supported') return null;
        const reg = await navigator.serviceWorker.getRegistration('/');
        return reg ? await reg.pushManager.getSubscription() : null;
    } catch { return null; }
}

/**
 * Ask the browser, subscribe, tell the server. MUST run inside the click
 * handler of "Yes, tell me" (iPhone only allows the prompt from a tap).
 * Resolves 'subscribed' | 'denied' | 'unsupported' | 'error'.
 */
export async function turnOnPush(email) {
    if (pushSupport() !== 'supported') return 'unsupported';
    try {
        const result = await window.Notification.requestPermission();
        if (result !== 'granted') return 'denied';
        const cfg = await getPushConfig(email);
        if (!cfg?.success || !cfg.public_key) return 'error';
        const reg = await registration();
        await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.subscribe({
            userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(cfg.public_key),
        });
        const res = await pushSubscribe(email, sub.toJSON(), deviceLabel());
        return res?.success ? 'subscribed' : 'error';
    } catch {
        return 'error';
    }
}

/** Unsubscribe this device here and on the server. */
export async function turnOffPush(email) {
    try {
        const sub = await currentSubscription();
        if (!sub) return true;
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        const res = await pushUnsubscribe(email, endpoint);
        return Boolean(res?.success);
    } catch {
        return false;
    }
}

// The C1 ask, once in a while: a "Not now" waits 14 days; a "Yes" or a
// browser "Block" ends the asking on this device.
const ASKED_KEY = 'astra_push_asked_at';
const WAIT_DAYS = 14;
export function mayAsk() {
    if (permission() !== 'default') return false;
    try {
        const t = Number(localStorage.getItem(ASKED_KEY) || 0);
        return !t || Date.now() - t > WAIT_DAYS * 864e5;
    } catch { return true; }
}
export function markAsked() {
    try { localStorage.setItem(ASKED_KEY, String(Date.now())); } catch { /* private mode */ }
}

// The useful moment (C1): a school just landed on the list. addSchool.js
// dispatches it after a confirmed add; the sheet listens.
export const SCHOOL_ADDED_EVENT = 'stratia:school-added';
export function announceSchoolAdded(school) {
    try { window.dispatchEvent(new CustomEvent(SCHOOL_ADDED_EVENT, { detail: school })); } catch { /* old browsers */ }
}
